import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "design-system/molecules/Chart"
import { ChartCard } from "design-system/organisms/insights/ChartCard"
import { formatKnowledgeRate } from "modules/backoffice/lib/knowledge/formatKnowledgeRate"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts"
import { BackofficeKnowledgeTimelineResponseDataSchema } from "schema"
import { z } from "zod"

type Timeline = z.infer<typeof BackofficeKnowledgeTimelineResponseDataSchema>

type Props = {
  data: Timeline | undefined
  isPending: boolean
}

const dayFormatter = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" })

export function BackofficeKnowledgeTimeline({ data, isPending }: Props) {
  const { t } = useTranslation()

  const chartConfig = useMemo(() => {
    return {
      knowledgeCoverageRate: {
        label: t("backoffice.knowledge.metrics.knowledgeCoverageRate"),
        color: "var(--chart-1)",
      },
      knowledgeBaseCoverageRate: {
        label: t("backoffice.knowledge.metrics.knowledgeBaseCoverageRate"),
        color: "var(--chart-4)",
      },
      interimCoverageRate: {
        label: t("backoffice.knowledge.metrics.interimCoverageRate"),
        color: "var(--chart-2)",
      },
    } satisfies ChartConfig
  }, [t])

  const chartData = useMemo(() => {
    return (data ?? []).map((item) => ({
      label: dayFormatter.format(new Date(`${item.day}T00:00:00`)),
      knowledgeCoverageRate: item.knowledgeCoverageRate,
      knowledgeBaseCoverageRate: item.knowledgeBaseCoverageRate,
      interimCoverageRate: item.interimCoverageRate,
    }))
  }, [data])

  const hasData = chartData.some(
    (item) =>
      item.knowledgeCoverageRate != null ||
      item.knowledgeBaseCoverageRate != null ||
      item.interimCoverageRate != null,
  )

  const chart = (
    <ChartContainer config={chartConfig} className="h-72 w-full">
      <LineChart accessibilityLayer data={chartData} margin={{ left: 8, right: 12 }}>
        <CartesianGrid vertical={false} />
        <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} minTickGap={48} />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={(value) => formatKnowledgeRate(Number(value))}
          width={52}
          domain={[0, 105]}
          ticks={[0, 25, 50, 75, 100]}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              formatter={(value, name) => {
                const metric = String(name)
                const label =
                  metric === "knowledgeCoverageRate" ||
                  metric === "knowledgeBaseCoverageRate" ||
                  metric === "interimCoverageRate"
                    ? chartConfig[metric].label
                    : metric

                return `${label}: ${formatKnowledgeRate(Number(value))}`
              }}
            />
          }
        />
        <ChartLegend content={<ChartLegendContent />} />
        <Line
          type="monotone"
          dataKey="interimCoverageRate"
          stroke="var(--color-interimCoverageRate)"
          strokeWidth={2}
          dot={{ r: 2 }}
        />
        <Line
          type="monotone"
          dataKey="knowledgeBaseCoverageRate"
          stroke="var(--color-knowledgeBaseCoverageRate)"
          strokeWidth={2}
          dot={{ r: 2 }}
        />
        <Line
          type="monotone"
          dataKey="knowledgeCoverageRate"
          stroke="var(--color-knowledgeCoverageRate)"
          strokeWidth={2}
          dot={{ r: 2 }}
        />
      </LineChart>
    </ChartContainer>
  )

  return (
    <ChartCard
      title={t("backoffice.knowledge.timeline.title")}
      description={t("backoffice.knowledge.timeline.description")}
      chart={chart}
      isPending={isPending}
      autoHeight
      shouldScrollToEnd={false}
      noData={{
        show: !isPending && !hasData,
        title: t("backoffice.knowledge.timeline.empty"),
        description: t("backoffice.knowledge.timeline.emptyDescription"),
      }}
    />
  )
}
